import type { BillingInterval, PlanPrice } from "@giftforward/entitlements";
import { PLAN_INTERVAL_STRINGS } from "@/constants/Plans";
import { offeredIntervals } from "@/utils/plan-price-helper";

const PILL_CLASSES =
  "cursor-pointer rounded-lg border px-5 py-2.5 font-lato text-[14px] text-rodeo-black transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark-dust focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-wax disabled:opacity-40";

/**
 * Monthly / Annual choice at checkout — the upgrade drawer and the onboarding payment drawer.
 *
 * Rendered only when the plan is sold at more than one interval (Pro). A plan sold one way (Pro
 * Plus, annual) shows no pills rather than a choice of one; which intervals exist is the
 * catalogue's offers, not a rule here.
 */
export default function BillingIntervalPills({
  offers,
  value,
  onChange,
  disabled = false,
}: {
  offers: readonly PlanPrice[];
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  disabled?: boolean;
}) {
  const intervals = offeredIntervals(offers);
  if (intervals.length < 2) return null;

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="font-lato text-[12px] font-bold uppercase tracking-[1.5px] text-dust-ink">
        {PLAN_INTERVAL_STRINGS.pillsLabel}
      </span>
      {/* Toggle buttons (aria-pressed), the repo's segmented-control pattern. */}
      <div className="inline-flex flex-wrap gap-2">
        {intervals.map((interval) => {
          const selected = interval === value;
          return (
            <button
              key={interval}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onChange(interval)}
              className={`${PILL_CLASSES} ${
                selected ? "border-dark-dust bg-clear-dust font-bold" : "border-smoke bg-white"
              }`}
            >
              {PLAN_INTERVAL_STRINGS.pill[interval]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
